import { useCallback, useEffect, useMemo } from "react"
import type { SlotData } from "@/app/booking-calendar/lib/dayGrid"
import { DayView } from "@/app/booking-calendar/components/DayView"
import { ScheduleLoadingSkeleton, ScheduleToolbar } from "@/app/booking-calendar/components/ScheduleHeader"
import {
  type ActiveBooking,
  type PlacedPairing,
  type SlotDecoration,
  guardBookedCell,
  targetValidity,
} from "@/app/booking-calendar/lib/rescheduleTargets"
import {
  type CalendarDate,
  longDateLabel,
  SITE_TIME_ZONE,
  todayInZone,
  zonedTimeToInstant,
} from "@/app/booking-calendar/lib/siteTime"
import { stepWeekday, weekdayInView } from "@/app/booking-calendar/lib/staffDay"
import { useTeamDay } from "@/app/booking-calendar/lib/useTeamDay"
import { useManualDay } from "../../../schedule-overrides/scheduleOverridesApi"
import type { Pairing } from "../lib/pairingState"

/** How often the day's rules are read again while the page is open, so "now" (and the notice) keeps up. */
export const MANUAL_DAY_REFRESH_MS = 60_000

/**
 * The right of the manual reschedule workspace (AB#811): the admin calendar's team-day grid for one
 * site and date — one column per host — in selection mode. Each cell is judged for the active booking
 * by targetValidity against the manual day read; a valid cell pairs it, a pending one undoes its
 * pairing, an invalid one says why on hover and takes no click. Steps a weekday at a time in the
 * page's own state. Mounted only once the site's Cal team id is known: useTeamDay falls back to the
 * first team for an id it does not know.
 */
export function ResolveDayGrid({
  overrideId,
  teamId,
  teamSlug,
  date,
  onDateChange,
  active,
  placed,
  onPair,
  onUnpair,
  disabled = false,
}: {
  overrideId: string
  teamId: number
  teamSlug: string
  date: CalendarDate
  onDateChange: (date: CalendarDate) => void
  active: ActiveBooking | null
  /** The pairings by target host and Pacific day (pairingsByHostDay). */
  placed: ReadonlyMap<string, readonly PlacedPairing[]>
  onPair: (requestId: string, target: Pairing) => void
  onUnpair: (requestId: string) => void
  /** While a batch runs: drawn, but nothing can be paired or undone. */
  disabled?: boolean
}) {
  const day = useTeamDay(date, teamId)
  const { columns, schedule, slotMinutes, status, slotsLoading, columnUnavailability } = day
  const timeZone = day.team?.timeZone || SITE_TIME_ZONE

  const manual = useManualDay(overrideId, teamSlug, date, { pollingInterval: MANUAL_DAY_REFRESH_MS })
  const manualDay = manual.day
  const { refetch, isUninitialized } = manual

  // Back to the page after a while away: the day's "now" has moved on.
  useEffect(() => {
    const onFocus = () => {
      if (!isUninitialized) void refetch()
    }
    window.addEventListener("focus", onFocus)
    return () => window.removeEventListener("focus", onFocus)
  }, [refetch, isUninitialized])

  const columnsById = useMemo(() => new Map(columns.map(column => [column.id, column])), [columns])

  const decorate = useCallback((columnId: string, _slotIdx: number, cell: SlotData): SlotDecoration | undefined => {
    const column = columnsById.get(columnId)
    if (!column) return undefined
    const decoration = targetValidity({
      active,
      day: manualDay,
      date,
      dayFailed: !manualDay && manual.isError,
      calUserId: column.calUserId,
      startMinutes: cell.startMinutes,
      slotMinutes,
      pairings: placed,
      timeZone,
    })
    // What Cal has booked there is never offered, whatever CMS's own list says.
    return guardBookedCell(decoration, cell, active)
  }, [columnsById, active, manualDay, date, manual.isError, slotMinutes, placed, timeZone])

  function click(columnId: string, slotIdx: number) {
    if (disabled) return
    const cell = schedule[columnId]?.[slotIdx]
    const column = columnsById.get(columnId)
    if (!cell || !column) return
    const decoration = decorate(columnId, slotIdx, cell)
    if (decoration?.kind === "pending" && decoration.requestId) {
      onUnpair(decoration.requestId)
      return
    }
    if (decoration?.kind === "valid" && active && manualDay) {
      onPair(active.requestId, {
        calUserId: column.calUserId,
        start: zonedTimeToInstant(manualDay.date, cell.startMinutes, timeZone).toISOString(),
        durationMinutes: active.durationMinutes,
        label: active.label,
        hostLabel: column.name,
      })
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="resolve-day-grid" data-team-slug={teamSlug} data-date={date}>
      <ScheduleToolbar
        onNavDay={(delta) => onDateChange(stepWeekday(date, delta))}
        onGoToday={() => onDateChange(weekdayInView(todayInZone(timeZone)))}
        onRefresh={() => {
          day.refresh()
          if (!isUninitialized) void refetch()
        }}
        refreshing={day.isFetching || manual.isFetching}
        loading={slotsLoading}
        teamLabel={day.team?.name ?? teamSlug}
        dayLabel={longDateLabel(date)}
        isToday={date === todayInZone(timeZone)}
        bookingCount={day.bookingCount}
        outsideWindowCount={day.outsideWindowCount}
        unhostedCount={day.unhostedCount}
        awayCount={Object.keys(columnUnavailability).length}
        slotMinutes={slotMinutes}
      />

      {status === "failed" && <p role="alert" className="px-1 text-xs text-red-700">{day.error}</p>}
      {manual.isError && (
        <p role="alert" className="px-1 text-xs text-red-700">
          This day&apos;s rules could not be read, so no slot can be offered. Try refreshing.
        </p>
      )}

      {status === "loading" ? (
        <ScheduleLoadingSkeleton />
      ) : (
        <DayView
          schedule={schedule}
          columns={columns}
          columnUnavailability={columnUnavailability}
          loading={slotsLoading}
          onSlotClick={click}
          decorate={decorate}
        />
      )}
    </div>
  )
}
